import {
    ChangeDetectionStrategy,
    Component,
    effect,
    ElementRef,
    Input,
    OnDestroy,
    signal,
    viewChild,
    ViewEncapsulation,
} from '@angular/core';
import { isNullOrUndefined } from '@fi-app/shared/utils/is-null-or-undefined';
import { ITaskV2AdditionalData } from '@fi-app/task-v2/fi-task-v2';
import { IMediaBankFileReference } from '@fi-shared/media-bank';
import { FiTaskV2AutomatedDescriptionComponent } from '../fi-task-v2-automated-description/fi-task-v2-automated-description.component';

@Component({
    selector: 'fi-task-v2-description-accordion',
    templateUrl: './fi-task-v2-description-accordion.component.html',
    styleUrls: ['./fi-task-v2-description-accordion.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'fi-task-v2-description-accordion' },
    standalone: false,
})
export class FiTaskV2DescriptionAccordionComponent implements OnDestroy {
    @Input({ required: true }) description: string = '';
    @Input() isAutomatedTask: boolean = false;
    @Input() additionalData: ITaskV2AdditionalData;
    @Input() mediaBankFileReferences: IMediaBankFileReference[];

    private regularTaskDescription = viewChild<ElementRef<HTMLElement>>('regularTaskDescription');
    private regularTaskDescriptionWidth = signal<string>('100%');
    private _setRegularTaskDescriptionWidthEffect = effect(() => {
        const el = this.regularTaskDescription();
        if (isNullOrUndefined(el)) {
            return;
        }
        const ro = new ResizeObserver(([entry]) => {
            this.regularTaskDescriptionWidth.set(`${entry.contentRect.width}px`);
        });

        ro.observe(el.nativeElement);

        return () => ro.disconnect();
    });

    automatedTaskDescription = viewChild<FiTaskV2AutomatedDescriptionComponent>('automatedTaskDescription');
    private checklistDescriptionWidth = signal<string>('100%');
    private additionalTaskDescriptionWidth = signal<string>('100%');
    private automatedTaskResizeObservers: ResizeObserver[] = [];
    private checkIntervalId: ReturnType<typeof setInterval> | null = null;
    private checkTimeoutId: ReturnType<typeof setTimeout> | null = null;

    private _automatedTaskDescriptionEffect = effect(() => {
        const component = this.automatedTaskDescription();
        if (!isNullOrUndefined(component)) {
            requestAnimationFrame(() => {
                this.setupAutomatedTaskResizeObservers(component);
            });
        } else {
            this.cleanupAutomatedTaskObservers();
        }
    });

    private setupAutomatedTaskResizeObservers(component: FiTaskV2AutomatedDescriptionComponent): void {
        this.cleanupAutomatedTaskObservers();

        const observedElements = new Set<HTMLElement>();

        const setupResizeObserver = (
            elementRef: ElementRef<HTMLElement> | undefined,
            widthSignal: typeof this.checklistDescriptionWidth,
        ): boolean => {
            if (elementRef?.nativeElement && !observedElements.has(elementRef.nativeElement)) {
                const ro = new ResizeObserver(([entry]) => {
                    widthSignal.set(`${entry.contentRect.width}px`);
                });
                ro.observe(elementRef.nativeElement);
                this.automatedTaskResizeObservers.push(ro);
                observedElements.add(elementRef.nativeElement);
                return true;
            }
            return observedElements.has(elementRef?.nativeElement);
        };

        const trySetupBoth = (): boolean => {
            const checklistObserving = setupResizeObserver(
                component.checklistDescriptionEl,
                this.checklistDescriptionWidth,
            );
            const additionalObserving = setupResizeObserver(
                component.additionalTaskDescriptionEl,
                this.additionalTaskDescriptionWidth,
            );
            return checklistObserving && additionalObserving;
        };

        if (trySetupBoth()) {
            return;
        }

        this.checkIntervalId = setInterval(() => {
            if (trySetupBoth()) {
                this.stopPolling();
            }
        }, 50);

        this.checkTimeoutId = setTimeout(() => {
            this.stopPolling();
        }, 5000);
    }

    private stopPolling(): void {
        if (this.checkIntervalId) {
            clearInterval(this.checkIntervalId);
            this.checkIntervalId = null;
        }
        if (this.checkTimeoutId) {
            clearTimeout(this.checkTimeoutId);
            this.checkTimeoutId = null;
        }
    }

    private cleanupAutomatedTaskObservers(): void {
        this.automatedTaskResizeObservers.forEach((ro) => ro.disconnect());
        this.automatedTaskResizeObservers = [];
        this.stopPolling();
    }

    protected getRollupContentMaxHeight(): number {
        if (this.isAutomatedTask) {
            const checklistData = this.additionalData?.ChecklistData;
            if (!this.description?.length && !this.mediaBankFileReferences?.length) {
                return !!checklistData?.CheckpointAnswerComments?.length ? 108 : 100;
            }
            if (!!this.description?.length && !this.mediaBankFileReferences?.length) {
                return !!checklistData?.CheckpointAnswerComments?.length ? 205 : 197;
            }
            if (
                !checklistData.CheckpointAnswerMediaBankFileReferences?.length &&
                !!this.mediaBankFileReferences?.length
            ) {
                return !!checklistData?.CheckpointAnswerComments?.length ? 205 : 197;
            }

            if (!!checklistData.CheckpointAnswerMediaBankFileReferences && !!this.mediaBankFileReferences?.length) {
                return !!checklistData?.CheckpointAnswerComments?.length ? 245 : 237;
            }
        } else {
            return 100;
        }
    }

    protected getBackdropWidth(): string {
        if (this.isAutomatedTask) {
            const hasDescription = !!this.description?.length;
            const hasMediaBankFileReferences = !!this.mediaBankFileReferences?.length;

            if (hasDescription && hasMediaBankFileReferences) {
                return this.additionalTaskDescriptionWidth();
            }

            if (hasDescription && !hasMediaBankFileReferences) {
                return '100%';
            }

            if (!hasDescription && !hasMediaBankFileReferences) {
                const checklistData = this.additionalData?.ChecklistData;
                if (!!checklistData?.CheckpointAnswerMediaBankFileReferences?.length) {
                    return this.checklistDescriptionWidth();
                }
                return '100%';
            }
        }
        return this.regularTaskDescriptionWidth();
    }

    public ngOnDestroy(): void {
        this.cleanupAutomatedTaskObservers();
    }
}
